import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpenCheck, CheckCircle2, Flame, GraduationCap, Target } from "lucide-react";
import { PageHeader } from "@/components/AppShell";
import { Spinner } from "@/components/Gate";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { CourseCard } from "@/components/learner/CourseCard";
import { errMsg, useApi } from "@/lib/api";

const Stat = ({ icon: Icon, label, value, testid }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-5">
    <Icon className="h-5 w-5 text-[#222d99]" />
    <p className="mt-4 font-display text-3xl font-extrabold text-slate-900" data-testid={testid}>{value}</p>
    <p className="text-xs text-slate-500">{label}</p>
  </div>
);

export default function LearnerDashboard() {
  const api = useApi();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    api.get("/learn/dashboard").then((r) => setD(r.data)).catch((e) => setErr(errMsg(e)));
  }, [api]);
  if (err) return <p className="text-rose-600">{err}</p>;
  if (!d) return <Spinner />;
  const cur = d.current;
  return (
    <div>
      <PageHeader eyebrow="Learner dashboard" title={`Hi ${d.name?.split(" ")[0] || "there"}`} testid="dashboard-greeting" />
      <div className="grid gap-4 md:grid-cols-4">
        <div className="rise rounded-2xl bg-[#222d99] p-6 text-white md:col-span-2 md:row-span-2 sm:p-8" data-testid="current-course-card">
          <p className="font-mono-label text-xs uppercase tracking-[0.2em] text-indigo-200">Continue learning</p>
          {cur ? (
            <>
              <h2 className="mt-3 text-2xl font-bold">{cur.course.title}</h2>
              <div className="mt-6 flex items-center justify-between text-sm"><span>Progress</span><span data-testid="current-course-progress">{cur.progress.percent}%</span></div>
              <Progress value={cur.progress.percent} className="mt-2 h-2 bg-white/20 [&>div]:bg-white" />
              {cur.progress.next_lesson && <p className="mt-6 text-sm text-indigo-100">Next: <span className="font-semibold text-white">{cur.progress.next_lesson.title}</span></p>}
              <Button asChild className="mt-6 rounded-full bg-white text-[#222d99] hover:bg-indigo-50" data-testid="resume-lesson-btn">
                <Link to={`/learn/lessons/${cur.resume_lesson_id}`}>Resume <ArrowRight className="ml-1 h-4 w-4" /></Link>
              </Button>
            </>
          ) : (
            <>
              <h2 className="mt-3 text-2xl font-bold">Pick your first course</h2>
              <p className="mt-2 text-sm text-indigo-100">Start with something matched to your target role: {d.profile.target_role}.</p>
              <Button asChild className="mt-6 rounded-full bg-white text-[#222d99] hover:bg-indigo-50" data-testid="browse-courses-btn">
                <Link to="/learn/courses">Browse courses <ArrowRight className="ml-1 h-4 w-4" /></Link>
              </Button>
            </>
          )}
        </div>
        <Stat icon={BookOpenCheck} label="Courses enrolled" value={d.stats.enrolled} testid="stat-enrolled" />
        <Stat icon={CheckCircle2} label="Lessons completed" value={d.stats.lessons_completed} testid="stat-lessons-completed" />
        <Stat icon={Flame} label="Day streak" value={d.stats.streak_days} testid="stat-streak" />
        <Stat icon={GraduationCap} label="Courses completed" value={d.stats.completed_courses} testid="stat-completed-courses" />
      </div>

      <div className="mt-6 flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-5 text-sm text-slate-600">
        <Target className="h-5 w-5 shrink-0 text-[#222d99]" />
        <span>Target role: <strong className="text-slate-900" data-testid="dashboard-target-role">{d.profile.target_role}</strong>.</span>
      </div>

      {d.recommended.length > 0 && (
        <section className="mt-12">
          <h2 className="mb-5 text-xl font-bold text-slate-900">Recommended next</h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="recommended-courses">
            {d.recommended.map((c) => <CourseCard key={c.id} c={c} testid={`recommended-course-${c.id}`} />)}
          </div>
        </section>
      )}
    </div>
  );
}
